/**
 * AI governance API access layer.
 *
 * Components receive an axios instance from `useApi()` and call these functions;
 * they never build SDA URLs or unwrap envelopes themselves. Keeping that here
 * means a route or response-shape change is a one-file edit, and every caller
 * gets the same normalized data.
 */

/** SDA wraps every response as `{ success, message, data }`. */
const unwrap = (response, fallbackMessage) => {
  const body = response?.data;
  if (body?.success) {
    return body.data;
  }
  const error = new Error(body?.error || body?.message || fallbackMessage);
  error.apiMessage = body?.error || body?.message;
  throw error;
};

/* ---------------------------------------------------------------- models ---- */

/**
 * Models available for selection, newest pricing included.
 * @param {object} api axios instance from useApi()
 * @param {{ includeInactive?: boolean }} [options] includeInactive is honoured
 *   for JLL Super Admin only; the API downgrades it for everyone else.
 */
export const fetchAiModels = async (api, { includeInactive = false } = {}) => {
  const response = await api.get('/ai/models', {
    params: includeInactive ? { includeInactive: true } : {},
  });
  const data = unwrap(response, 'Failed to load AI models');
  return data?.models || [];
};

export const createAiModel = async (api, payload) => {
  const response = await api.post('/ai/models', payload);
  return unwrap(response, 'Failed to create AI model');
};

export const updateAiModel = async (api, modelCode, payload) => {
  const response = await api.put(`/ai/models/${encodeURIComponent(modelCode)}`, payload);
  return unwrap(response, 'Failed to update AI model');
};

export const updateAiModelPricing = async (api, modelCode, payload) => {
  const response = await api.put(`/ai/models/${encodeURIComponent(modelCode)}/pricing`, payload);
  return unwrap(response, 'Failed to update AI model pricing');
};

export const setAiModelActive = async (api, modelCode, isActive) => {
  const response = await api.put(`/ai/models/${encodeURIComponent(modelCode)}/status`, { isActive });
  return unwrap(response, 'Failed to change AI model status');
};

export const fetchAiModelPricingHistory = async (api, modelCode) => {
  const response = await api.get(`/ai/models/${encodeURIComponent(modelCode)}/pricing`);
  const data = unwrap(response, 'Failed to load pricing history');
  return data?.pricing || [];
};

/* ---------------------------------------------------------- token limits ---- */

/** One client's allowance. `isConfigured` is false when none was ever set. */
export const fetchClientTokenLimit = async (api, tenantId) => {
  const response = await api.get(`/ai/token-limits/${tenantId}`);
  return unwrap(response, 'Failed to load AI token limits');
};

export const saveClientTokenLimit = async (api, tenantId, payload) => {
  const response = await api.put(`/ai/token-limits/${tenantId}`, payload);
  return unwrap(response, 'Failed to save AI token limits');
};

/* ----------------------------------------------------------------- usage ---- */

/**
 * Usage for the tenant the API client is scoped to (the signed-in user's own
 * client), or for `tenantId` when a JLL Super Admin inspects another client.
 *
 * `canViewCost` reflects the server's decision: cost figures are stripped from
 * the payload for anyone below Client Admin, so the UI follows this flag rather
 * than re-deriving the rule from the user's role.
 *
 * @returns {Promise<{ summary: object, byModel: object[], canViewCost: boolean,
 *                     scope: string }>}
 */
export const fetchAiUsage = async (api, { tenantId = null, allTime = false } = {}) => {
  const path = tenantId ? `/ai/usage/${tenantId}` : '/ai/usage';
  const response = await api.get(path, { params: allTime ? { allTime: true } : {} });
  const data = unwrap(response, 'Failed to load AI usage');
  return {
    summary: data?.summary || null,
    byModel: data?.byModel || [],
    canViewCost: !!data?.canViewCost,
    scope: data?.scope || 'currentCycle',
  };
};

/** Cross-client usage for the JLL master tenant. */
export const fetchAiUsageOverview = async (
  api,
  { page = 1, pageSize = 10, search = '', configuredOnly = false } = {},
) => {
  const params = { page, pageSize };
  if (search) params.search = search;
  if (configuredOnly) params.configuredOnly = true;

  const response = await api.get('/ai/usage/overview', { params });
  const data = unwrap(response, 'Failed to load client AI usage');
  return {
    clients: data?.clients || [],
    totals: data?.totals || { totalCost: 0, totalInputTokens: 0, totalOutputTokens: 0 },
    pagination: data?.pagination || { page, pageSize, totalPages: 1, totalItems: 0 },
  };
};

/** JLL advanced action: restart a client's allowance cycle at 0%. */
export const resetAiUsageCycle = async (api, tenantId) => {
  const response = await api.post(`/ai/usage/${tenantId}/reset-cycle`);
  return unwrap(response, 'Failed to reset the AI usage cycle');
};
